import { Box, type SxProps, type Theme } from "@mui/material";
import { type FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { useJobColors } from "../../../hooks/useJobColors";
import { useDepartmentStaffCountByJobId } from "../../../hooks/useStaffCountByJobId";
import { sortJobsByDisplayName, sortJobsByPopularity } from "../../../utils/jobs";
import type { Job } from "../../../utils/models";
import { type SystemChipItem, SystemChips } from "../Clickable/TabsAndChips/SystemChips/SystemChips";
import { SystemDropdown } from "../Inputs/SystemDropDown/SystemDropdown";
import type { DropdownItem } from "../Inputs/SystemDropDown/types";

type JobSelectorProps = {
	jobs: Job[];
	selectedJobIds: string[];
	onSelectedJobsChanged: (selected: string[]) => void;
	/** Hide the "All" option */
	hideAllOption?: boolean;
	/** Single-select mode: clicking one deselects the other */
	singleSelect?: boolean;
	variant?: "chips" | "dropdown";
	/** Dropdown variant only — label rendered inside the field */
	label?: string;
	/** Dropdown variant only — text shown while nothing is chosen. Absent = none, as today. */
	placeholder?: string;
	/** Dropdown variant only — sx applied to the underlying Select */
	selectSx?: SxProps<Theme>;
	/** Dropdown variant only — sx applied to the underlying form control */
	formControlSx?: SxProps<Theme>;
	/** Dropdown variant only — make the trigger fill its containing block */
	fullWidth?: boolean;
	/** Dropdown variant only — fires when the menu opens */
	onOpen?: () => void;
	/** Dropdown variant only — fires when the menu closes */
	onClose?: () => void;
	/**
	 * Dropdown variant only — test id of the dropdown. Defaults to the long-standing `job-dropdown`, so a
	 * screen with two job pickers (e.g. "replace this job" / "with this one") can tell them apart.
	 */
	dataTestId?: string;
	/** Whose staff decide the popularity order — set when the page shows another department (the ops backoffice drill-down). */
	staffCountDepartmentId?: string;
};

export const JobSelector: FC<JobSelectorProps> = ({
	jobs,
	selectedJobIds,
	onSelectedJobsChanged,
	hideAllOption = false,
	singleSelect = false,
	variant = "chips",
	label,
	placeholder,
	selectSx,
	formControlSx,
	fullWidth,
	onOpen,
	onClose,
	// DEFAULT: the id every existing caller (and its e2e specs) already targets.
	dataTestId = "job-dropdown",
	staffCountDepartmentId,
}) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	// DEFAULT: every page but Management Center's drill-down shows the chosen department.
	const staffCountByJobId = useDepartmentStaffCountByJobId(staffCountDepartmentId ?? chosenDepartment.department_id);
	const jobColor = useJobColors();

	const allValue = useMemo<SystemChipItem<"all">>(() => ({ value: "all", label: t("system.dropdown.all") }), [t]);

	// Options are ordered by popularity — how many of the department's staff hold each job — so the
	// jobs an admin actually schedules sit first instead of wherever the alphabet put them.
	const sortedJobs = useMemo(() => {
		// DEFAULT: there are exactly two ways to have no popularity signal — the roster has not
		// arrived yet on first paint, and a department where nobody holds a job. Alphabetical is the
		// honest answer for both: any other order would imply a ranking we cannot compute, and it is
		// what every other job list in the app shows, so the fallback is never a surprise.
		if (staffCountByJobId === null) return sortJobsByDisplayName(jobs);
		return sortJobsByPopularity(jobs, staffCountByJobId);
	}, [jobs, staffCountByJobId]);

	const jobItems: SystemChipItem<string>[] = useMemo(
		() => [
			...(hideAllOption ? [] : [allValue]),
			...sortedJobs.map((job) => ({
				value: job.id,
				label: job.displayName,
				startAdornment: (
					<Box sx={{ borderRadius: "50%", backgroundColor: `${jobColor(job.id).dot} !important`, width: "10px !important", height: "10px !important" }} />
				),
			})),
		],
		[sortedJobs, allValue, hideAllOption, jobColor],
	);

	const allJobItems = useMemo(() => jobItems.filter((c) => c.value !== "all"), [jobItems]);
	const isAllSelected = !hideAllOption && (selectedJobIds.length === 0 || selectedJobIds.length === jobs.length);

	const selectedItems: SystemChipItem<string>[] = useMemo(() => {
		if (isAllSelected) return [allValue];
		return jobItems.filter((c) => selectedJobIds.includes(c.value));
	}, [isAllSelected, allValue, jobItems, selectedJobIds]);

	const handleChange = useCallback(
		(value: SystemChipItem<string>, selected: boolean) => {
			if (value.value === "all") {
				onSelectedJobsChanged(allJobItems.map((c) => c.value));
				return;
			}
			if (singleSelect) {
				onSelectedJobsChanged(selected ? [value.value] : []);
				return;
			}
			if (isAllSelected && selected) {
				onSelectedJobsChanged([value.value]);
				return;
			}
			let newIds: string[];
			if (selected) {
				newIds = [...selectedJobIds, value.value];
				if (newIds.length === allJobItems.length) {
					onSelectedJobsChanged(allJobItems.map((c) => c.value));
					return;
				}
			} else {
				newIds = selectedJobIds.filter((id) => id !== value.value);
			}
			onSelectedJobsChanged(newIds);
		},
		[selectedJobIds, onSelectedJobsChanged, allJobItems, isAllSelected, singleSelect],
	);

	const dropdownItems = useMemo<DropdownItem<string>[]>(
		() =>
			sortedJobs.map((job) => ({
				value: job.id,
				label: job.displayName,
				color: jobColor(job.id).dot,
			})),
		[sortedJobs, jobColor],
	);

	if (variant === "dropdown") {
		if (singleSelect) {
			return (
				<SystemDropdown
					items={dropdownItems}
					selectedItem={selectedJobIds[0]}
					onChange={(id) => onSelectedJobsChanged(id ? [id] : [])}
					label={label}
					placeholder={placeholder}
					selectSx={selectSx}
					formControlSx={formControlSx}
					fullWidth={fullWidth}
					onOpen={onOpen}
					onClose={onClose}
					data-testid={dataTestId}
				/>
			);
		}
		return (
			<SystemDropdown
				multiple
				items={dropdownItems}
				selectedItems={selectedJobIds}
				onChangeMultiple={onSelectedJobsChanged}
				label={label}
				placeholder={placeholder}
				selectSx={selectSx}
				onOpen={onOpen}
				onClose={onClose}
				formControlSx={formControlSx}
				fullWidth={fullWidth}
				data-testid={dataTestId}
			/>
		);
	}

	return <SystemChips items={jobItems} onChange={handleChange} selectedItems={selectedItems} forceFitContent={true} dataTestId="job-chips-container" />;
};
